import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Bell, CalendarDays } from "lucide-react";
import { ImageResizable } from "../../../components/elements/ImageResizeable";
import { formatCurrency } from "../../../utils/currency";
import { AURORA_DESTINATIONS } from "../../../constants/aurora";
import { AURORA_ANCHORS, AURORA_THEME } from "./theme";
import { ownPageLinkProps } from "../../../utils/appShell";

// Aurora's single trip-inventory section. Renders 4 destination cards (per Maomi's
// brief: "reserve space for 4 trip cards in total"). Each card is anchored to a
// project; if that project has live bookable trips we surface availability + dates
// + spot-left tag, otherwise we render a "coming soon" placeholder. There is no
// separate live-trips grid below — this is the only inventory surface on /c/aurora.

const STATUS_COPY = {
  available: "campaign.aurora.spotlight.available",
  coming_soon: "campaign.aurora.spotlight.coming_soon",
};

// Spot tags only render when stock is genuinely low — high-contrast amber/red
// per Maomi's brief ("剩 3 個名額"). Threshold matches the existing TripCard logic.
const LOW_STOCK_THRESHOLD = 5;

const SpotlightCard = ({ destination, tripData, index }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { key, status, image, projectId } = destination;
  // A destination is bookable as soon as it has a project page — live trip data
  // from the campaign API only enriches the card (dates strip, price, spot tags).
  const isAvailable = status === "available" && Boolean(projectId);
  const hasLiveTrips = isAvailable && Boolean(tripData);

  const dateCount = tripData?.all_dates?.length || 0;
  const spotsLeft = tripData?.spotsLeft;
  const isSoldOut = tripData?.soldOut;
  const showLowStock =
    hasLiveTrips &&
    !isSoldOut &&
    typeof spotsLeft === "number" &&
    spotsLeft <= LOW_STOCK_THRESHOLD;

  const href = isAvailable
    ? `${window.location.origin}/trips/${projectId}`
    : `#${AURORA_ANCHORS.faqs}`;

  const cardProps = isAvailable ? ownPageLinkProps(navigate, `/trips/${projectId}`) : {};

  return (
    <motion.a
      href={href}
      {...cardProps}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 * index, duration: 0.4 }}
      whileHover={{ y: -4 }}
      className="tw-group tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl"
      style={{
        backgroundColor: AURORA_THEME.bg.surface,
        boxShadow: AURORA_THEME.glow.soft,
        border: "1px solid rgba(0, 255, 204, 0.12)",
      }}
    >
      {/* Borderless hero image — extends edge-to-edge per the brief. */}
      <div className="tw-relative tw-h-48 tw-w-full tw-overflow-hidden sm:tw-h-52 lg:tw-h-64">
        {image ? (
          <ImageResizable
            size="oxl"
            src={image}
            alt={t(`campaign.aurora.spotlight.${key}.name`)}
            className="tw-h-full tw-w-full tw-object-cover tw-transition-transform group-hover:tw-scale-105"
          />
        ) : (
          <div
            className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-6xl lg:tw-text-7xl"
            style={{ background: AURORA_THEME.gradients.hero }}
          >
            <span role="img" aria-label="Canada">🇨🇦</span>
          </div>
        )}
        {/* Soft fade so the image bleeds into the card surface — mimics the
            brief's "fluid gradient transitions" without hard edges. */}
        <div
          aria-hidden="true"
          className="tw-absolute tw-inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,16,38,0) 45%, rgba(20,26,58,0.95) 100%)",
          }}
        />
        <div
          className="tw-absolute tw-left-3 tw-top-3 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-backdrop-blur-md"
          style={{
            backgroundColor: isAvailable
              ? "rgba(0, 255, 204, 0.18)"
              : "rgba(0, 255, 204, 0.08)",
            color: AURORA_THEME.accent.primary,
            border: `1px solid ${AURORA_THEME.accent.primary}`,
            boxShadow: isAvailable ? "0 0 12px rgba(0, 255, 204, 0.4)" : "none",
          }}
        >
          {t(STATUS_COPY[isAvailable ? "available" : "coming_soon"])}
        </div>
        {showLowStock && (
          <div
            className="tw-absolute tw-right-3 tw-top-3 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white tw-backdrop-blur-md"
            style={{
              backgroundColor: "rgba(255, 92, 141, 0.95)",
              boxShadow: "0 0 12px rgba(255, 92, 141, 0.45)",
            }}
          >
            {t("campaign.aurora.spot_tag.left", { count: spotsLeft })}
          </div>
        )}
        {hasLiveTrips && isSoldOut && (
          <div
            className="tw-absolute tw-right-3 tw-top-3 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white tw-backdrop-blur-md"
            style={{
              backgroundColor: "rgba(120, 120, 120, 0.85)",
            }}
          >
            {t("campaign.aurora.spot_tag.sold_out")}
          </div>
        )}
      </div>

      {/* Content — name, tagline, optional dates strip, CTA */}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-px-5 tw-py-5 lg:tw-px-6 lg:tw-py-6">
        <div className="tw-min-w-0">
          <h3 className="tw-text-lg tw-font-bold tw-text-white md:tw-text-xl">
            {t(`campaign.aurora.spotlight.${key}.name`)}
          </h3>
          <p
            className="tw-mt-1.5 tw-line-clamp-2 tw-text-sm"
            style={{ color: AURORA_THEME.text.secondary }}
          >
            {t(`campaign.aurora.spotlight.${key}.tagline`)}
          </p>

          {hasLiveTrips && (
            <div className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-text-xs">
              <CalendarDays
                className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-flex-shrink-0"
                style={{ color: AURORA_THEME.accent.primary }}
              />
              <div className="tw-flex tw-flex-col tw-gap-0.5">
                <span style={{ color: AURORA_THEME.text.primary }}>
                  {t("campaign.aurora.spotlight.dates_count", { count: dateCount })}
                </span>
                {tripData?.all_dates?.[0] && (
                  <span style={{ color: AURORA_THEME.text.muted }}>
                    {tripData.all_dates[0]}
                    {dateCount > 1 ? " …" : ""}
                  </span>
                )}
              </div>
            </div>
          )}

          {hasLiveTrips && tripData?.price && (
            <p
              className="tw-mt-3 tw-text-xs"
              style={{ color: AURORA_THEME.text.muted }}
            >
              <span>{t("campaign.aurora.spotlight.from")}</span>{" "}
              <span
                className="tw-text-base tw-font-bold"
                style={{ color: AURORA_THEME.accent.primary }}
              >
                {formatCurrency(tripData.price)}
              </span>
            </p>
          )}
        </div>

        <div
          className="tw-mt-4 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold"
          style={{ color: AURORA_THEME.accent.primary }}
        >
          {isAvailable ? (
            <>
              <span>{t("campaign.aurora.spotlight.view_trips")}</span>
              <ArrowRight className="tw-h-4 tw-w-4 tw-transition-transform group-hover:tw-translate-x-0.5" />
            </>
          ) : (
            <>
              <Bell className="tw-h-4 tw-w-4" />
              <span>{t("campaign.aurora.spotlight.notify_me")}</span>
            </>
          )}
        </div>
      </div>
    </motion.a>
  );
};

const AuroraDestinationSpotlight = ({ trips = [] }) => {
  const { t } = useTranslation();

  // Map destinations → live trip data (when available) so each card knows whether
  // to render the rich availability strip or the "coming soon" placeholder.
  const tripsByProject = trips.reduce((acc, trip) => {
    acc[trip.project_id] = trip;
    return acc;
  }, {});

  return (
    <section
      id={AURORA_ANCHORS.spotlight}
      className="tw-relative tw-w-full tw-py-12 md:tw-py-16 lg:tw-py-20"
    >
      <div className="tw-mx-auto tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8 2xl:tw-max-w-[1440px]">
        <div className="tw-mb-10 tw-text-center md:tw-mb-12">
          <h2 className="tw-text-2xl tw-font-bold tw-text-white md:tw-text-3xl lg:tw-text-4xl">
            {t("campaign.aurora.spotlight.title")}
          </h2>
          <p
            className="tw-mt-3 tw-text-sm md:tw-text-base lg:tw-text-lg"
            style={{ color: AURORA_THEME.text.secondary }}
          >
            {t("campaign.aurora.spotlight.subtitle")}
          </p>
        </div>
        <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2 sm:tw-gap-6 lg:tw-grid-cols-4 lg:tw-gap-7">
          {AURORA_DESTINATIONS.map((dest, idx) => (
            <SpotlightCard
              key={dest.key}
              destination={dest}
              tripData={dest.projectId ? tripsByProject[dest.projectId] : null}
              index={idx}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AuroraDestinationSpotlight;
